import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useOptionalAppSession } from './AppSessionContext';
import {
  calculateStepProgress,
  DEFAULT_DAILY_STEP_GOAL,
  localDayKey,
  normalizeDailyStepGoal,
  shouldCountMotionSample,
  stepStorageKey,
} from '../steps/stepCounterCore.mjs';

const StepsContext = createContext(null);
const SENSOR_BOOT_TIMEOUT_MS = 5000;

function normalizeInitialSteps(initialSteps) {
  if (typeof initialSteps === 'number') return Math.max(0, initialSteps);
  if (initialSteps && typeof initialSteps === 'object') {
    return Math.max(0, Number(initialSteps.todaySteps ?? initialSteps.today_steps ?? 0) || 0);
  }
  return 0;
}

function readStoredSession(userId, dayKey) {
  try {
    const parsed = JSON.parse(localStorage.getItem(stepStorageKey(userId, dayKey)) || '{}');
    return {
      steps: Math.max(0, Number(parsed.steps || 0)),
      startedAt: Number(parsed.startedAt || 0) || null,
      sessionId: String(parsed.sessionId || ''),
    };
  } catch {
    return { steps: 0, startedAt: null, sessionId: '' };
  }
}

function writeStoredSession(userId, dayKey, session) {
  try {
    localStorage.setItem(stepStorageKey(userId, dayKey), JSON.stringify({
      steps: Math.max(0, Number(session.steps || 0)),
      startedAt: Number(session.startedAt || 0) || null,
      sessionId: String(session.sessionId || ''),
    }));
  } catch {}
}

function removeStoredSession(userId, dayKey) {
  try { localStorage.removeItem(stepStorageKey(userId, dayKey)); } catch {}
}

function createSessionId() {
  if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID();
  return `steps-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

export function StepsProvider({ children, initialSteps = null }) {
  const sessionContext = useOptionalAppSession();
  const runtime = typeof window !== 'undefined' ? window.pfandpiratData || {} : {};
  const userId = String(runtime.user_id || sessionContext?.session?.user?.id || 'guest');
  const initialDay = localDayKey();
  const initialStored = readStoredSession(userId, initialDay);
  const configuredGoal = sessionContext?.session?.steps?.dailyGoal
    ?? sessionContext?.session?.steps?.daily_goal
    ?? runtime.stepGoal
    ?? runtime.step_goal;
  const dailyGoal = normalizeDailyStepGoal(configuredGoal, DEFAULT_DAILY_STEP_GOAL);

  const [dayKey, setDayKey] = useState(initialDay);
  const [persistedDailySteps, setPersistedDailySteps] = useState(() => normalizeInitialSteps(initialSteps));
  const [sessionSteps, setSessionSteps] = useState(initialStored.steps);
  const [totalSteps, setTotalSteps] = useState(() => Number(initialSteps?.totalSteps ?? initialSteps?.total_steps ?? 0) || 0);
  const [streak, setStreak] = useState(() => Number(initialSteps?.streak || 0) || 0);
  const [status, setStatus] = useState(initialStored.steps > 0 ? 'paused' : 'not_started');
  const [motionPermission, setMotionPermission] = useState('unknown');
  const [trackingError, setTrackingError] = useState('');
  const [sensorReady, setSensorReady] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [pageVisible, setPageVisible] = useState(() => typeof document === 'undefined' || document.visibilityState !== 'hidden');

  const sessionStepsRef = useRef(initialStored.steps);
  const sessionStartRef = useRef(initialStored.startedAt);
  const sessionIdRef = useRef(initialStored.sessionId);
  const lastStepAtRef = useRef(0);
  const listenerRef = useRef(null);
  const sensorTimeoutRef = useRef(null);
  const statusRef = useRef(status);
  const dayKeyRef = useRef(dayKey);

  const requiresMotionPermission = typeof window !== 'undefined'
    && typeof window.DeviceMotionEvent !== 'undefined'
    && typeof window.DeviceMotionEvent.requestPermission === 'function';

  const clearSensorTimeout = useCallback(() => {
    if (sensorTimeoutRef.current) {
      window.clearTimeout(sensorTimeoutRef.current);
      sensorTimeoutRef.current = null;
    }
  }, []);

  const detachSensor = useCallback(() => {
    clearSensorTimeout();
    if (listenerRef.current) {
      window.removeEventListener('devicemotion', listenerRef.current);
      listenerRef.current = null;
    }
  }, [clearSensorTimeout]);

  const persistLocalSession = useCallback((override = {}) => {
    writeStoredSession(userId, dayKeyRef.current, {
      steps: override.steps ?? sessionStepsRef.current,
      startedAt: override.startedAt ?? sessionStartRef.current,
      sessionId: override.sessionId ?? sessionIdRef.current,
    });
  }, [userId]);

  const postSession = useCallback(async ({ steps, date, startedAt, sessionId }) => {
    const amount = Math.max(0, Number(steps || 0));
    if (!amount || !runtime.stepsApiUrl || userId === 'guest') return false;
    const endedAt = Date.now();
    const response = await fetch(runtime.stepsApiUrl, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'X-WP-Nonce': runtime.nonce || '',
      },
      body: JSON.stringify({
        steps: amount,
        date,
        duration: startedAt ? Math.max(0, endedAt - startedAt) : 0,
        user_id: Number(userId),
        session_id: sessionId || createSessionId(),
        start_time: startedAt ? new Date(startedAt).toISOString() : new Date(endedAt).toISOString(),
        end_time: new Date(endedAt).toISOString(),
      }),
    });
    if (!response.ok) throw new Error('Schrittstand konnte nicht gespeichert werden.');
    const data = await response.json().catch(() => ({}));
    const expeditionStepCharge = data?.expedition_step_charge;
    if (expeditionStepCharge?.scanner_energy_added > 0) {
      window.dispatchEvent(new CustomEvent('pfandpirat:expedition-step-charge', { detail: expeditionStepCharge }));
    }
    return true;
  }, [runtime.nonce, runtime.stepsApiUrl, userId]);

  const flushSession = useCallback(async () => {
    const snapshot = {
      steps: sessionStepsRef.current,
      date: dayKeyRef.current,
      startedAt: sessionStartRef.current,
      sessionId: sessionIdRef.current,
    };
    if (!snapshot.steps) return true;
    writeStoredSession(userId, snapshot.date, snapshot);
    try {
      const saved = await postSession(snapshot);
      if (!saved) return false;
      setPersistedDailySteps((current) => current + snapshot.steps);
      setTotalSteps((current) => current + snapshot.steps);
      sessionStepsRef.current = 0;
      sessionStartRef.current = null;
      sessionIdRef.current = '';
      setSessionSteps(0);
      removeStoredSession(userId, snapshot.date);
      setTrackingError('');
      return true;
    } catch {
      setTrackingError('Deine Schritte bleiben lokal auf diesem Gerät gespeichert und werden beim nächsten Pausieren erneut übertragen.');
      return false;
    }
  }, [postSession, userId]);

  const rolloverDayIfNeeded = useCallback(() => {
    const currentDay = localDayKey();
    if (!currentDay || currentDay === dayKeyRef.current) return;
    const previous = {
      steps: sessionStepsRef.current,
      date: dayKeyRef.current,
      startedAt: sessionStartRef.current,
      sessionId: sessionIdRef.current,
    };
    if (previous.steps) {
      void postSession(previous).catch(() => writeStoredSession(userId, previous.date, previous));
    }
    dayKeyRef.current = currentDay;
    setDayKey(currentDay);
    setPersistedDailySteps(0);
    setSessionSteps(0);
    sessionStepsRef.current = 0;
    sessionStartRef.current = statusRef.current === 'active' ? Date.now() : null;
    sessionIdRef.current = statusRef.current === 'active' ? createSessionId() : '';
    persistLocalSession({ steps: 0, startedAt: sessionStartRef.current, sessionId: sessionIdRef.current });
  }, [persistLocalSession, postSession, userId]);

  useEffect(() => { statusRef.current = status; }, [status]);
  useEffect(() => { dayKeyRef.current = dayKey; }, [dayKey]);

  useEffect(() => {
    const hasInitialSteps = initialSteps !== null && initialSteps !== undefined;
    if (hasInitialSteps) {
      setPersistedDailySteps(normalizeInitialSteps(initialSteps));
      setTotalSteps(Number(initialSteps?.totalSteps ?? initialSteps?.total_steps ?? 0) || 0);
      setStreak(Number(initialSteps?.streak || 0) || 0);
      return undefined;
    }
    if (userId === 'guest') return undefined;
    let cancelled = false;
    fetch('/wp-json/pfandpirat/v1/steps/summary', {
      credentials: 'include',
      cache: 'no-store',
      headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache', 'X-WP-Nonce': runtime.nonce || '' },
    })
      .then((response) => response.json())
      .then((data) => {
        if (cancelled) return;
        setPersistedDailySteps(Number(data?.todaySteps ?? data?.today_steps ?? 0) || 0);
        setTotalSteps(Number(data?.totalSteps ?? data?.total_steps ?? 0) || 0);
        setStreak(Number(data?.streak || 0) || 0);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [initialSteps, runtime.nonce, userId]);

  const requestMotionPermission = useCallback(async () => {
    if (!window.isSecureContext) {
      setStatus('https_required');
      setMotionPermission('unavailable');
      setTrackingError('Der Schrittzähler benötigt eine sichere HTTPS-Verbindung.');
      return false;
    }
    if (typeof window.DeviceMotionEvent === 'undefined') {
      setStatus('unsupported');
      setMotionPermission('unsupported');
      setTrackingError('Dieses Gerät oder dieser Browser stellt keinen Bewegungssensor bereit.');
      return false;
    }
    if (!requiresMotionPermission) {
      setMotionPermission('granted');
      return true;
    }
    setStatus('requesting');
    try {
      const permission = await window.DeviceMotionEvent.requestPermission();
      if (permission !== 'granted') {
        setMotionPermission('denied');
        setStatus('denied');
        setTrackingError('Die Freigabe für Bewegungssensoren wurde nicht erteilt.');
        return false;
      }
      setMotionPermission('granted');
      return true;
    } catch {
      setMotionPermission('error');
      setStatus('error');
      setTrackingError('Die Bewegungsfreigabe konnte nicht abgefragt werden.');
      return false;
    }
  }, [requiresMotionPermission]);

  const startTracking = useCallback(async () => {
    if (statusRef.current === 'active' || statusRef.current === 'requesting') return;
    setTrackingError('');
    setSensorReady(false);
    const granted = motionPermission === 'granted' || await requestMotionPermission();
    if (!granted) return;
    rolloverDayIfNeeded();
    if (!sessionStartRef.current) sessionStartRef.current = Date.now();
    if (!sessionIdRef.current) sessionIdRef.current = createSessionId();
    persistLocalSession();
    setStatus('active');
  }, [motionPermission, persistLocalSession, requestMotionPermission, rolloverDayIfNeeded]);

  const pauseTracking = useCallback(async () => {
    if (statusRef.current !== 'active') return;
    detachSensor();
    setStatus('paused');
    setSensorReady(false);
    persistLocalSession();
    await flushSession();
  }, [detachSensor, flushSession, persistLocalSession]);

  const clearTemporaryState = useCallback(() => {
    detachSensor();
    removeStoredSession(userId, dayKeyRef.current);
    sessionStepsRef.current = 0;
    sessionStartRef.current = null;
    sessionIdRef.current = '';
    setSessionSteps(0);
    setStatus('not_started');
    setSensorReady(false);
  }, [detachSensor, userId]);

  useEffect(() => {
    if (status !== 'active' || !pageVisible) {
      detachSensor();
      return undefined;
    }
    if (listenerRef.current) return undefined;
    const handler = (event) => {
      clearSensorTimeout();
      setSensorReady(true);
      setTrackingError('');
      const timestamp = Number(event.timeStamp) > 0 ? performance.timeOrigin + Number(event.timeStamp) : Date.now();
      if (!shouldCountMotionSample({ event, timestamp, lastStepAt: lastStepAtRef.current })) return;
      lastStepAtRef.current = timestamp;
      rolloverDayIfNeeded();
      sessionStepsRef.current += 1;
      setSessionSteps(sessionStepsRef.current);
      persistLocalSession();
    };
    listenerRef.current = handler;
    window.addEventListener('devicemotion', handler, { passive: true });
    sensorTimeoutRef.current = window.setTimeout(() => {
      detachSensor();
      setSensorReady(false);
      setStatus('error');
      setTrackingError('Der Browser liefert keine Bewegungsdaten. Prüfe die Geräte- und Browserfreigaben und versuche es erneut.');
    }, SENSOR_BOOT_TIMEOUT_MS);
    return detachSensor;
  }, [clearSensorTimeout, detachSensor, pageVisible, persistLocalSession, rolloverDayIfNeeded, status]);

  useEffect(() => {
    const onVisibilityChange = () => {
      const visible = document.visibilityState !== 'hidden';
      setPageVisible(visible);
      persistLocalSession();
      if (visible) rolloverDayIfNeeded();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    const timer = window.setInterval(rolloverDayIfNeeded, 60000);
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.clearInterval(timer);
    };
  }, [persistLocalSession, rolloverDayIfNeeded]);

  useEffect(() => {
    if (status !== 'active') return undefined;
    const timer = window.setInterval(() => {
      const startedAt = sessionStartRef.current;
      setElapsed(startedAt ? Math.max(0, Math.floor((Date.now() - startedAt) / 1000)) : 0);
    }, 1000);
    return () => window.clearInterval(timer);
  }, [status]);

  useEffect(() => () => {
    detachSensor();
    persistLocalSession();
  }, [detachSensor, persistLocalSession]);

  const addSteps = useCallback((count) => {
    const amount = Number(count || 0);
    if (!Number.isFinite(amount) || amount <= 0) return;
    setPersistedDailySteps((current) => current + amount);
    setTotalSteps((current) => current + amount);
    if (sessionContext?.mergeSessionPatch) {
      const previous = sessionContext.session?.steps || {};
      sessionContext.mergeSessionPatch({
        steps: {
          ...previous,
          todaySteps: Number(previous.todaySteps || persistedDailySteps || 0) + amount,
          totalSteps: Number(previous.totalSteps || totalSteps || 0) + amount,
        },
      });
    }
    if (runtime.stepsApiUrl && userId !== 'guest') {
      void postSession({ steps: amount, date: dayKeyRef.current, startedAt: Date.now(), sessionId: `manual-${Date.now()}` }).catch(() => {});
    }
  }, [persistedDailySteps, postSession, runtime.stepsApiUrl, sessionContext, totalSteps, userId]);

  const steps = persistedDailySteps + sessionSteps;
  const progress = calculateStepProgress(steps, dailyGoal);
  const value = useMemo(() => ({
    steps,
    dailySteps: steps,
    persistedDailySteps,
    sessionSteps,
    totalSteps: totalSteps + sessionSteps,
    streak,
    dailyGoal,
    DAILY_STEP_GOAL: dailyGoal,
    progress,
    goalReachedToday: steps >= dailyGoal,
    status,
    isTracking: status === 'active',
    isPaused: status === 'paused',
    isRequesting: status === 'requesting',
    backgroundSuspended: status === 'active' && !pageVisible,
    elapsed,
    motionPermission,
    trackingError,
    sensorReady,
    requiresMotionPermission,
    startTracking,
    resumeTracking: startTracking,
    pauseTracking,
    stopTracking: pauseTracking,
    addSteps,
    clearTemporaryState,
  }), [addSteps, clearTemporaryState, dailyGoal, elapsed, motionPermission, pageVisible, pauseTracking, persistedDailySteps, progress, requiresMotionPermission, sensorReady, sessionSteps, startTracking, status, steps, streak, totalSteps, trackingError]);

  return <StepsContext.Provider value={value}>{children}</StepsContext.Provider>;
}

export function useStepsContext() {
  const context = useContext(StepsContext);
  if (!context) throw new Error('useStepsContext muss innerhalb des StepsProvider verwendet werden.');
  return context;
}
